import { Fragment, type ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { StepChainItem } from "@/components/design/StepChain";
import type { LivePart, TurnInput } from "@/lib/games/roleplay/game-store";
import { markedParts, paragraphs, type StoryItem, type StoryMark } from "@/lib/games/roleplay/story";

// The story as one text (O3): openings, the player's turns with their narrations, the closing, then whatever is
// streaming now, with no heading or divider between scenes. From the player's first own move on the story runs down a
// timeline of turn cards (spark#1236): a thin neutral line on the left and, hanging on it, one card per turn, the
// player's move and the narration that answers it, so the player's own words and the story's never run together and
// the newest turn is said in text. The opening before the first move stands on the plain surface, no card, no line.
// Every text here is untrusted plain text from
// agent-service or the player and is rendered as text nodes only: never as markup, never as markdown. Narration reads
// in ink like Save Studio's notes; while the story works, its line looks like Studio's running work, so the
// game and Studio are one UI: an item of the shared step chain with the running title's shimmer, ink only.
//
// An item the player has just got, and a character who has just come into the story, is marked where the narration that
// brought them names them (82): `marks` holds their words per run, `liveMarks` those of the run
// streaming now. The text is only cut at those words; every piece stays a text node, the marked words inside a mark
// element (index.css, mark.grow-news).
//
// `unsent` is a move that was not stored while the player had already written on in the field (23.1): it stands where
// it would have stood, in a card of its own said to be not saved, so the player can still take its words.

const NO_MARKS: readonly StoryMark[] = [];

function Paragraphs({ text, tone, marks = NO_MARKS }: { text: string; tone: string; marks?: readonly StoryMark[] }) {
  return (
    <>
      {paragraphs(text).map((paragraph, index) => (
        <p key={index} className={`whitespace-pre-line ${tone}`}>
          {marks.length === 0
            ? paragraph
            : markedParts(paragraph, marks).map((part, at) =>
                part.marked ? (
                  <mark key={at} className="grow-news">
                    {part.text}
                  </mark>
                ) : (
                  <Fragment key={at}>{part.text}</Fragment>
                ),
              )}
        </p>
      ))}
    </>
  );
}

function Narration({ text, marks }: { text: string; marks?: readonly StoryMark[] }) {
  return (
    <div className="space-y-4">
      <Paragraphs text={text} tone="text-ink" marks={marks} />
    </div>
  );
}

interface Card {
  key: string;
  number: number;
  note: ReactNode;
  move: string;
  body: ReactNode[];
}

// One turn on the timeline (spark#1236): a card with "Zug N" in its header, the player's move as a quote, then the
// narration that answers it. A screen reader hears the card as an article named "Zug N" ("Zug N, neu" for the newest);
// the header says the same, so it is hidden from it, and the dot on the line is only drawn. N is the turn's number in
// the game as the log stored it, so after a log cut to its last turns the first card still says its real number.
// `note` stands between the header and the move: the pole a turn sent before 79 carries, or that the move was not saved.
// The move is shown without the spaces and line breaks at its start and end: Enter starts a new line in the field, so a
// move is often sent with a line break at its end, and the quote's closing mark would stand alone on the next line.
//
// The dot sits on the line's centre: the line's 1px starts 4px into the timeline's pl-4, so its centre is 4.5px from
// the timeline's edge and a 9px dot starts at that edge, the card's 16px plus its own left padding to the left of the
// card's header. The header's 18px row puts the dot's centre 21px below the card's top, where the line starts.
function TurnCard({ card, newest }: { card: Card; newest: boolean }) {
  const intl = useIntl();
  const name = newest
    ? intl.formatMessage({ id: "grow.roleplay.story.turnNewName", defaultMessage: "Turn {n}, new" }, { n: card.number })
    : intl.formatMessage({ id: "grow.roleplay.story.turn", defaultMessage: "Turn {n}" }, { n: card.number });
  const move = card.move.trim();
  return (
    <article aria-label={name} className="box-flat bg-page space-y-3 p-3 @sm:px-4">
      <div aria-hidden="true" className="relative flex min-h-[18px] items-center justify-between gap-3">
        <span
          data-dot={newest ? "filled" : "hollow"}
          className={`absolute -left-[28px] top-1/2 size-[9px] -translate-y-1/2 rounded-full border border-ink @sm:-left-8 ${
            newest ? "bg-ink" : "bg-surface"
          }`}
        />
        <p className="smycles-field-label text-ink-muted">
          <FormattedMessage id="grow.roleplay.story.turn" defaultMessage="Turn {n}" values={{ n: card.number }} />
        </p>
        {newest ? (
          <p className="smycles-field-meta text-ink-muted">
            <FormattedMessage id="grow.roleplay.story.new" defaultMessage="New" />
          </p>
        ) : null}
      </div>
      {card.note ? <p className="smycles-field-meta text-ink-muted">{card.note}</p> : null}
      {move ? (
        <p className="whitespace-pre-line font-medium text-ink">
          <FormattedMessage id="grow.roleplay.story.move" defaultMessage="“{text}”" values={{ text: move }} />
        </p>
      ) : null}
      {card.body}
    </article>
  );
}

export function StoryFeed({
  items,
  live,
  pending,
  unsent = null,
  truncated,
  hint,
  marks,
  liveMarks = NO_MARKS,
}: {
  items: readonly StoryItem[];
  live: readonly LivePart[];
  pending: TurnInput | null;
  unsent?: TurnInput | null;
  truncated: boolean;
  hint: ReactNode;
  marks?: ReadonlyMap<string, readonly StoryMark[]>;
  liveMarks?: readonly StoryMark[];
}) {
  // The opening before the first own move stands on its own; every own move opens a card that holds the narration
  // after it, up to the next move, so the closing lands in the last card.
  const opening: ReactNode[] = [];
  const cards: Card[] = [];
  // A move not stored yet comes after the last stored turn.
  const next = () => (cards.at(-1)?.number ?? 0) + 1;
  for (const item of items) {
    if (item.kind === "player") {
      cards.push({ key: item.key, number: item.turn, note: item.pole, move: item.text, body: [] });
      continue;
    }
    const narration = <Narration key={item.key} text={item.text} marks={marks?.get(item.runId)} />;
    (cards.at(-1)?.body ?? opening).push(narration);
  }
  if (pending) cards.push({ key: "pending", number: next(), note: null, move: pending.text, body: [] });
  // The newest turn is the one the story answers last: the move being sent and answered now, or else the last stored
  // one. What streams now and the hint belong to it; before any move they stand with the opening.
  const newest = cards.at(-1);
  const now = [
    ...live.map((part, index) => <Narration key={`live-${index}`} text={part.text} marks={liveMarks} />),
    hint ? (
      <div key="hint" role="status">
        <StepChainItem>
          <span className="smycles-shimmer">{hint}</span>
        </StepChainItem>
      </div>
    ) : null,
  ];
  (newest?.body ?? opening).push(...now);
  // A move that was not saved is not part of the story, so it never takes "Neu" from them: it gets a card of its own,
  // with a hollow dot and its note.
  const notSaved: Card | null = unsent
    ? {
        key: "unsent",
        number: next(),
        note: <FormattedMessage id="grow.roleplay.story.unsent" defaultMessage="Not saved" />,
        move: unsent.text,
        body: [],
      }
    : null;

  return (
    <div className="space-y-4 text-smycles-body leading-relaxed">
      {truncated ? (
        <p className="smycles-field-meta text-ink-muted">
          <FormattedMessage
            id="grow.roleplay.story.truncated"
            defaultMessage="Earlier parts of the story are no longer shown."
          />
        </p>
      ) : null}
      {opening}
      {newest || notSaved ? (
        <div data-testid="story-timeline" className="relative space-y-3 pl-4">
          <span aria-hidden="true" data-line="" className="absolute bottom-0 left-1 top-[21px] border-l border-ink/15" />
          {cards.map((card) => (
            <TurnCard key={card.key} card={card} newest={card === newest} />
          ))}
          {notSaved ? <TurnCard card={notSaved} newest={false} /> : null}
        </div>
      ) : null}
    </div>
  );
}
